import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Menu, X, ShieldCheck } from 'lucide-react';
import { scrollToId } from '../lib/scroll';
import { easeOut } from './Reveal';

const LINKS = [
  { id: 'supports', label: 'Supports' },
  { id: 'why-us', label: 'Why Us' },
  { id: 'areas', label: 'Service Area' },
  { id: 'apply', label: 'Join Us' },
  { id: 'contact', label: 'Contact' },
];

export const go = (id) => scrollToId(id);

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const jump = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/85 backdrop-blur-xl border-b border-sky-100 shadow-soft'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto flex items-center justify-between h-[76px] px-6 lg:px-12">
          <button
            data-testid="nav-logo-home"
            onClick={() => window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.2 }) : window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-3 text-left"
            aria-label="Care For All – Support Services, back to top"
          >
            <img
              src="/cfa-cover.png"
              alt="CFA Support Services logo"
              className="h-11 w-11 rounded-full bg-white object-cover ring-2 ring-sky-200 shadow-soft"
            />
            <span className="leading-tight">
              <span className={`block font-display font-extrabold text-lg lg:text-xl tracking-tight ${scrolled ? 'text-navy' : 'text-white'}`}>
                Care For All
              </span>
              <span className="flex items-center gap-2.5">
                <span className={`block font-display font-bold text-sm lg:text-[15px] tracking-tight ${scrolled ? 'text-azure' : 'text-cyan-300'}`}>
                  Support Services
                </span>
                <img
                  src="/ndis-logo.png"
                  alt="NDIS — National Disability Insurance Scheme"
                  data-testid="header-ndis-badge"
                  className="block h-5 w-auto rounded"
                />
              </span>
              <span className={`hidden lg:flex items-center gap-1.5 text-[8px] uppercase tracking-[0.3em] font-semibold mt-0.5 ${scrolled ? 'text-slate-400' : 'text-white/50'}`}>
                Empowering <span className="text-cyan-500">|</span> Supporting <span className="text-cyan-500">|</span> Inclusive
              </span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => jump(l.id)}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-azure ${
                  scrolled ? 'text-slate-700' : 'text-white/90 hover:text-white'
                }`}
              >
                {l.label}
              </button>
            ))}
            <a
              data-testid="nav-cta-call"
              href="tel:0473619278"
              className="group flex items-center gap-2 rounded-full bg-azure px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition-all hover:bg-sky-700 hover:shadow-lift"
            >
              <Phone size={15} className="transition-transform group-hover:rotate-12" />
              0473 619 278
            </a>
          </nav>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            className={`lg:hidden grid place-items-center h-11 w-11 rounded-full transition-colors ${
              scrolled ? 'bg-sky-100 text-navy' : 'bg-white/15 text-white backdrop-blur'
            }`}
          >
            <Menu size={20} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-navy text-white flex flex-col"
          >
            <div className="flex items-center justify-between h-[76px] px-6">
              <div className="flex items-center gap-3">
                <img src="/cfa-cover.png" alt="" className="h-10 w-10 rounded-full bg-white object-cover" />
                <span className="leading-tight">
                  <span className="block font-display font-extrabold text-lg tracking-tight">Care For All</span>
                  <span className="block font-display font-bold text-sm tracking-tight text-cyan-300">Support Services</span>
                </span>
              </div>
              <button
                data-testid="nav-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="grid place-items-center h-11 w-11 rounded-full bg-white/10"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.6, ease: easeOut }}
                  onClick={() => jump(l.id)}
                  className="text-left font-display font-extrabold text-4xl py-3 text-white/95 hover:text-cyan-300 transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <div className="flex items-center gap-2 text-cyan-300 text-xs uppercase tracking-[0.28em] font-semibold mb-3">
                <ShieldCheck size={14} /> Registered NDIS Provider
              </div>
              <a
                data-testid="nav-mobile-call"
                href="tel:0473619278"
                className="flex items-center justify-center gap-2 rounded-full bg-azure px-6 py-4 font-semibold"
              >
                <Phone size={17} /> Call 0473 619 278
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
